import { Trophy, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Using the API Football IDs as provided by the user
export const LEAGUES_CONFIG = [
  { id: 71, name: 'Brasil (Série A)', short: 'Brasileirão' },
  { id: 39, name: 'Inglaterra (Premier League)', short: 'Premier League' },
  { id: 61, name: 'França (Ligue 1)', short: 'Ligue 1' },
  { id: 78, name: 'Alemanha (Bundesliga)', short: 'Bundesliga' },
  { id: 135, name: 'Itália (Serie A)', short: 'Serie A' },
  { id: 88, name: 'Holanda (Eredivisie)', short: 'Eredivisie' },
  { id: 94, name: 'Portugal (Liga)', short: 'Liga Portugal' },
  { id: 140, name: 'Espanha (La Liga)', short: 'La Liga' },
  { id: 119, name: 'Dinamarca (Superliga)', short: 'Superliga DK' },
  { id: 179, name: 'Escócia (Premiership)', short: 'Premiership SC' },
  { id: 203, name: 'Turquia (Super Lig)', short: 'Super Lig' },
  { id: 40, name: 'Inglaterra (Championship)', short: 'Championship' },
  { id: 79, name: 'Alemanha (Bundesliga 2)', short: 'Bundesliga 2' },
  { id: 32, name: 'World Cup Qualification (Europe)', short: 'WC Qual.' },
  { id: 2, name: 'UEFA Champions League', short: 'UCL' },
  { id: 3, name: 'UEFA Europa League', short: 'UEL' },
  { id: 848, name: 'UEFA Conference League', short: 'UECL' },
  { id: 307, name: 'Arábia Saudita (Pro League)', short: 'Saudi Pro' },
  { id: 95, name: 'Portugal (Segunda Liga)', short: 'Segunda Liga' }
];

interface LeagueFilterProps {
  selectedLeagueId?: number;
  onLeagueChange: (leagueId: number | undefined) => void;
  className?: string;
  variant?: 'dropdown' | 'tabs';
}

export function LeagueFilter({
  selectedLeagueId,
  onLeagueChange,
  className,
  variant = 'dropdown'
}: LeagueFilterProps) {
  const selectedLeague = selectedLeagueId 
    ? LEAGUES_CONFIG.find(l => l.id === selectedLeagueId)
    : null;

  if (variant === 'tabs') {
    return (
      <div className={cn("overflow-x-auto hide-scrollbar", className)}>
        <div className="flex gap-2 pb-2 min-w-max px-1">
          <Button
            variant={!selectedLeagueId ? "default" : "outline"}
            size="default"
            onClick={() => onLeagueChange(undefined)}
            className="sketch-border hover-sketch font-bold text-sm whitespace-nowrap min-h-[44px] px-4 touch-manipulation"
          >
            Todas as Ligas
          </Button>
          {LEAGUES_CONFIG.map((league) => (
            <Button
              key={league.id}
              variant={selectedLeagueId === league.id ? "default" : "outline"}
              size="default"
              onClick={() => onLeagueChange(league.id)}
              className="sketch-border hover-sketch font-bold text-sm whitespace-nowrap min-h-[44px] px-4 touch-manipulation"
            >
              {league.short}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "sketch-border hover-sketch font-bold justify-between w-full min-w-[200px] min-h-[44px] touch-manipulation",
            className
          )}
        >
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-primary" />
            <span>
              {selectedLeague ? selectedLeague.short : "Todas as Ligas"}
            </span>
          </div>
          <ChevronDown size={16} className="text-gray-medium" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-[280px] bg-card border-gray-light sketch-border z-50"
        align="start"
        sideOffset={8}
      >
        <DropdownMenuItem
          onClick={() => onLeagueChange(undefined)}
          className={cn(
            "font-bold cursor-pointer hover:bg-secondary focus:bg-secondary min-h-[44px] py-3 px-4 touch-manipulation",
            !selectedLeagueId && "bg-secondary"
          )}
        >
          <Trophy size={14} className="mr-2 text-primary" />
          Todas as Ligas
        </DropdownMenuItem>
        
        {LEAGUES_CONFIG.map((league) => (
          <DropdownMenuItem
            key={league.id}
            onClick={() => onLeagueChange(league.id)}
            className={cn(
              "font-medium cursor-pointer hover:bg-secondary focus:bg-secondary min-h-[44px] py-3 px-4 touch-manipulation",
              selectedLeagueId === league.id && "bg-secondary"
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span>{league.short}</span>
              <span className="text-xs text-gray-medium">{league.name}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}